import React from "react";

const GOLD = "#C9A84C";
const NAVY = "#0b1f4e";

export const LegalLayout = ({ eyebrow, title, intro, updatedOn, children, testId }) => (
  <div className="min-h-screen bg-white text-black" data-testid={testId}>
    {/* Brand header */}
    <header
      className="sticky top-0 z-40 bg-white/95 backdrop-blur border-b"
      style={{ borderColor: `${GOLD}33` }}
      data-testid="legal-brand-header"
    >
      <a
        href="/"
        className="mx-auto max-w-3xl px-5 sm:px-8 h-14 flex items-center gap-3 group"
        data-testid="legal-brand-home-link"
      >
        <span className="text-[13px] sm:text-sm font-bold tracking-[0.25em] text-black group-hover:opacity-80 transition-opacity">
          AXIS
        </span>
        <span className="h-4 w-px" style={{ backgroundColor: GOLD }} aria-hidden="true" />
        <span className="text-[13px] sm:text-sm tracking-[0.18em] text-black/70 group-hover:text-black transition-colors">
          SmartPeptides
        </span>
      </a>
    </header>

    <main className="mx-auto max-w-3xl px-6 sm:px-8 pt-10 sm:pt-14 pb-16">
      {eyebrow && (
        <p
          className="text-[12px] font-semibold tracking-[0.28em] uppercase"
          style={{ color: GOLD }}
          data-testid="legal-eyebrow"
        >
          {eyebrow}
        </p>
      )}

      <h1
        className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight leading-[1.05]"
        data-testid="legal-title"
      >
        {title}
      </h1>

      {intro && (
        <p className="mt-8 text-[17px] sm:text-[18px] leading-relaxed text-black/85" data-testid="legal-intro">
          {intro}
        </p>
      )}

      {updatedOn && (
        <p
          className="mt-8 text-[11px] sm:text-xs font-semibold tracking-[0.28em] uppercase text-black/70"
          data-testid="legal-updated-on"
        >
          Last updated: {updatedOn}
        </p>
      )}

      <div
        className="mt-8 h-px w-full"
        style={{ background: `linear-gradient(to right, ${GOLD}66, ${GOLD}00)` }}
        aria-hidden="true"
      />

      <div className="mt-4">{children}</div>
    </main>

    {/* Brand footer */}
    <footer className="text-white" style={{ backgroundColor: NAVY }} data-testid="legal-brand-footer">
      <div className="mx-auto max-w-3xl px-6 sm:px-8 py-10">
        <div className="flex items-center gap-3">
          <span className="text-[13px] sm:text-sm font-bold tracking-[0.25em]">AXIS</span>
          <span className="h-4 w-px" style={{ backgroundColor: GOLD }} aria-hidden="true" />
          <span className="text-[13px] sm:text-sm tracking-[0.18em] text-white/70">SmartPeptides</span>
        </div>
        <p className="mt-4 text-2xl sm:text-3xl font-semibold tracking-tight leading-tight">
          Precision Peptides. Engineered for Results.
        </p>
        <div className="mt-8 pt-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 border-t border-white/15">
          <p className="text-[11px] tracking-[0.2em] uppercase text-white/60">
            © {new Date().getFullYear()} SmartPeptides.ai
          </p>
          <div className="flex gap-5 text-[11px] uppercase tracking-[0.22em] text-white/70">
            <a href="/privacy" className="hover:text-white transition-colors" data-testid="footer-privacy-link">Privacy</a>
            <a href="/terms" className="hover:text-white transition-colors" data-testid="footer-terms-link">Terms</a>
            <a href="mailto:hello@smartpeptides.ai" className="hover:text-white transition-colors" data-testid="footer-contact-link">Contact</a>
          </div>
        </div>
      </div>
    </footer>
  </div>
);

/* Numbered section — gold "01" label above bold heading, matches source design */
export const Section = ({ number, title, children, testId }) => (
  <section className="mt-14 sm:mt-16 first:mt-10" data-testid={testId || `section-${number}`}>
    <p
      className="text-[13px] font-semibold tracking-[0.32em]"
      style={{ color: GOLD }}
      aria-hidden="true"
    >
      {number}
    </p>
    <h2 className="mt-4 text-[26px] sm:text-[30px] font-semibold tracking-tight leading-[1.15]">
      {title}
    </h2>
    <div className="mt-5 space-y-5">{children}</div>
  </section>
);

export const P = ({ children }) => (
  <p className="text-[16px] sm:text-[17px] leading-[1.7] text-black/85">{children}</p>
);
